import React, { useState, useEffect } from 'react';
import * as THREE from 'three';

// Debug panel to visualize and identify IFC materials by color
export const IFCMaterialDebugPanel = ({ meshMapRef }) => {
  const [materials, setMaterials] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMesh, setSelectedMesh] = useState(null);
  const [textureScaleX, setTextureScaleX] = useState(1.0);
  const [textureScaleY, setTextureScaleY] = useState(1.0);
  const [uvScaleX, setUvScaleX] = useState(1.0);
  const [uvScaleY, setUvScaleY] = useState(1.0);

  // Predefined bright colors for easy identification
  const debugColors = [
    '#FF0000', // Red
    '#00FF00', // Green
    '#0000FF', // Blue
    '#FFFF00', // Yellow
    '#FF00FF', // Magenta
    '#00FFFF', // Cyan
    '#FF8000', // Orange
    '#8000FF', // Purple
    '#00FF80', // Spring Green
    '#FF0080', // Rose
    '#80FF00', // Lime
    '#0080FF', // Sky Blue
  ];

  useEffect(() => {
    // Poll for materials every 2 seconds
    const updateMaterials = () => {
      if (meshMapRef?.current) {
        const materialNames = Array.from(meshMapRef.current.keys());
        setMaterials(materialNames.map((name, index) => ({
          name,
          color: debugColors[index % debugColors.length],
          visible: true
        })));
      }
    };

    updateMaterials();
    const interval = setInterval(updateMaterials, 2000);
    return () => clearInterval(interval);
  }, [meshMapRef]);

  const applyColor = (materialName, color) => {
    if (!meshMapRef?.current) return;
    const mesh = meshMapRef.current.get(materialName);
    if (mesh) {
      mesh.material = new THREE.MeshStandardMaterial({
        color: color,
        roughness: 0.5,
        metalness: 0.1
      });
      mesh.material.needsUpdate = true;
    }
  };

  const toggleVisibility = (materialName) => {
    if (!meshMapRef?.current) return;
    const mesh = meshMapRef.current.get(materialName);
    if (mesh) {
      mesh.visible = !mesh.visible;
      setMaterials(prev => prev.map(m =>
        m.name === materialName ? { ...m, visible: mesh.visible } : m
      ));
    }
  };

  const applyAllColors = () => {
    materials.forEach(({ name, color }) => {
      applyColor(name, color);
    });
  };

  const resetAllColors = () => {
    if (!meshMapRef?.current) return;
    meshMapRef.current.forEach((mesh) => {
      // Restore original IFC color if available
      const originalColor = mesh.userData?.originalColor;
      if (originalColor) {
        mesh.material = new THREE.MeshStandardMaterial({
          color: new THREE.Color(originalColor.r, originalColor.g, originalColor.b),
          roughness: 0.7,
          metalness: 0.1,
          transparent: originalColor.a < 1,
          opacity: originalColor.a
        });
      } else {
        mesh.material = new THREE.MeshStandardMaterial({
          color: 0xcccccc,
          roughness: 0.7,
          metalness: 0.1
        });
      }
      mesh.material.needsUpdate = true;
    });
  };

  // Apply texture scale to selected mesh (separate X and Y)
  const applyTextureScale = (scaleX, scaleY) => {
    if (!meshMapRef?.current || !selectedMesh) return;
    const mesh = meshMapRef.current.get(selectedMesh);
    if (mesh && mesh.material) {
      // Update texture repeat on all texture maps
      const maps = ['map', 'normalMap', 'roughnessMap', 'aoMap', 'displacementMap'];
      maps.forEach(mapName => {
        if (mesh.material[mapName]) {
          mesh.material[mapName].repeat.set(scaleX, scaleY);
          mesh.material[mapName].needsUpdate = true;
        }
      });
      mesh.material.needsUpdate = true;
      console.log(`📐 Applied texture scale (${scaleX}, ${scaleY}) to ${selectedMesh}`);
    }
  };

  // Apply UV scale to selected mesh (separate X and Y)
  const applyUvScale = (scaleX, scaleY) => {
    if (!meshMapRef?.current || !selectedMesh) return;
    const mesh = meshMapRef.current.get(selectedMesh);
    if (mesh && mesh.geometry) {
      const geometry = mesh.geometry;
      const uvs = geometry.attributes.uv;
      if (uvs) {
        // Scale existing UVs
        const originalUvs = geometry.userData.originalUvs || uvs.array.slice();
        if (!geometry.userData.originalUvs) {
          geometry.userData.originalUvs = originalUvs;
        }
        const newUvs = new Float32Array(originalUvs.length);
        for (let i = 0; i < originalUvs.length; i += 2) {
          newUvs[i] = originalUvs[i] * scaleX;     // U
          newUvs[i + 1] = originalUvs[i + 1] * scaleY; // V
        }
        geometry.setAttribute('uv', new THREE.BufferAttribute(newUvs, 2));
        geometry.attributes.uv.needsUpdate = true;
        console.log(`📐 Applied UV scale (${scaleX}, ${scaleY}) to ${selectedMesh}`);
      }
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={{
          position: 'fixed',
          top: 10,
          left: 10,
          zIndex: 10000,
          padding: '8px 16px',
          background: '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          cursor: 'pointer',
          fontSize: 11
        }}
      >
        🔧 IFC Debug
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 10,
      left: 10,
      zIndex: 10000,
      background: 'rgba(0,0,0,0.9)',
      color: '#fff',
      padding: 16,
      borderRadius: 8,
      maxHeight: '80vh',
      overflowY: 'auto',
      minWidth: 300,
      maxWidth: 350,
      fontFamily: 'monospace',
      fontSize: 12
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>IFC Material Debug</h3>
        <button
          onClick={() => setIsOpen(false)}
          style={{
            background: 'none',
            border: 'none',
            color: '#fff',
            fontSize: 18,
            cursor: 'pointer'
          }}
        >
          ×
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button
          onClick={applyAllColors}
          style={{
            flex: 1,
            padding: '8px',
            background: '#4CAF50',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}
        >
          Apply All Colors
        </button>
        <button
          onClick={resetAllColors}
          style={{
            flex: 1,
            padding: '8px',
            background: '#666',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}
        >
          Reset
        </button>
      </div>

      <div style={{ marginBottom: 8, color: '#aaa' }}>
        {materials.length} materials found
      </div>

      {/* Texture Scale Controls */}
      {selectedMesh && (
        <div style={{
          background: '#1a1a2e',
          padding: 12,
          borderRadius: 4,
          marginBottom: 12,
          border: '1px solid #4CAF50'
        }}>
          <div style={{ marginBottom: 8, color: '#4CAF50', fontWeight: 'bold' }}>
            Selected: {selectedMesh.split('|')[0]}
          </div>

          {/* UV Scale X */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', marginBottom: 4 }}>
              UV Scale X (U): {uvScaleX.toFixed(6)}
            </label>
            <input
              type="range"
              min="0.00001"
              max="1000"
              step="0.001"
              value={uvScaleX}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setUvScaleX(val);
                applyUvScale(val, uvScaleY);
              }}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              {[0.001, 0.01, 0.1, 1, 10, 100, 1000].map(v => (
                <button
                  key={v}
                  onClick={() => { setUvScaleX(v); applyUvScale(v, uvScaleY); }}
                  style={{
                    flex: 1,
                    padding: '4px',
                    background: '#333',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 2,
                    cursor: 'pointer',
                    fontSize: 8
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* UV Scale Y */}
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', marginBottom: 4 }}>
              UV Scale Y (V): {uvScaleY.toFixed(6)}
            </label>
            <input
              type="range"
              min="0.00001"
              max="1000"
              step="0.001"
              value={uvScaleY}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setUvScaleY(val);
                applyUvScale(uvScaleX, val);
              }}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              {[0.001, 0.01, 0.1, 1, 10, 100, 1000].map(v => (
                <button
                  key={v}
                  onClick={() => { setUvScaleY(v); applyUvScale(uvScaleX, v); }}
                  style={{
                    flex: 1,
                    padding: '4px',
                    background: '#333',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 2,
                    cursor: 'pointer',
                    fontSize: 8
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Copy values button */}
          <button
            onClick={() => {
              const config = `unitsPerRepeatX: ${(1/uvScaleX).toFixed(0)}, unitsPerRepeatY: ${(1/uvScaleY).toFixed(0)}`;
              console.log(`📋 ${config}`);
              navigator.clipboard?.writeText(config);
            }}
            style={{
              width: '100%',
              padding: '8px',
              background: '#2196F3',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 11,
              marginTop: 4
            }}
          >
            Copy unitsPerRepeat values
          </button>
        </div>
      )}

      {materials.map(({ name, color, visible }) => (
        <div
          key={name}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 0',
            borderBottom: '1px solid #444'
          }}
        >
          <input
            type="color"
            value={color}
            onChange={(e) => {
              const newColor = e.target.value;
              setMaterials(prev => prev.map(m =>
                m.name === name ? { ...m, color: newColor } : m
              ));
              applyColor(name, newColor);
            }}
            style={{ width: 30, height: 24, border: 'none', cursor: 'pointer' }}
          />
          <button
            onClick={() => applyColor(name, color)}
            style={{
              background: color,
              color: '#000',
              border: 'none',
              borderRadius: 4,
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: 10
            }}
          >
            Set
          </button>
          <button
            onClick={() => toggleVisibility(name)}
            style={{
              background: visible ? '#2196F3' : '#666',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: 10
            }}
          >
            {visible ? 'Hide' : 'Show'}
          </button>
          <button
            onClick={() => {
              setSelectedMesh(name);
              setTextureScaleX(1.0);
              setTextureScaleY(1.0);
              setUvScaleX(0.01);
              setUvScaleY(0.01);
            }}
            style={{
              background: selectedMesh === name ? '#4CAF50' : '#555',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: 10
            }}
          >
            Edit
          </button>
          <span style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: visible ? '#fff' : '#666'
          }}>
            {name}
          </span>
        </div>
      ))}
    </div>
  );
};

export default IFCMaterialDebugPanel;
